import {TFunction} from 'i18next';
import {
  getContentProps,
  getImageUrl,
  I_ContentMeta,
} from '../../common/state/apiGet';
import {ITicketType} from '../../common/type';
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_SHORT_DATETIME_FORMAT,
  formatFromServerDate,
  getDuration,
  getRemainText,
  getTimeAgoText,
  getTimeStamp,
  SECONDS_OF_AN_HOUR,
  SECONDS_OF_A_DAY,
  SHORT_DATE_FORMAT,
  getNowFromServer,
} from '../../common/util/date';
import {parseLandingUriFromServer} from '../../common/util/PageRouter';
import {toCamelCase} from '../../common/util/string';
import {I_EpisodeItem} from '../../commonSub/state/apiGet';
import {IExpireDate} from '../../commonSub/type';
import {
  IAddedCashItem,
  IAddedCashType,
  IAddedTicketItem,
  IAddedDaumCashItem,
  IUsedDaumCashItem,
  IMyNews,
  IThirdPartyTerm,
  IUsedCashItem,
  IUsedCashType,
  IUsedTicketItem,
  IWaitfreeContent,
} from '../type';

export function getHasNewMore(data: I_MoreRedDotStatus): boolean {
  return Object.values(data).some(item => item);
}

interface I_MoreRedDotStatus {
  newsRedDot: boolean;
  noticeRedDot: boolean;
  waitForFreeRedDot: boolean;
}

export function getThirdPartyTerms(
  data: I_ThirdPartyTerm[]
): IThirdPartyTerm[] {
  return data.map(term => ({
    termId: term.termOfUseId,
    isAccepted: term.accepted === 'Y',
    termDate: term.termVersion,
  }));
}

export function getMyNewsList(
  t: TFunction,
  newsList: I_MyNews[] = []
): IMyNews[] {
  return newsList.map(
    ({
      id,
      readDateTime,
      title,
      body,
      thumbnailImage,
      data,
      landingUrl,
      receivedDateTime,
    }) => {
      let expireDate: string | IExpireDate | undefined;

      if (data?.duration) {
        expireDate = getDuration(data.duration);
      } else if (data?.expireDateTime) {
        expireDate = formatFromServerDate(
          data.expireDateTime,
          DEFAULT_DATE_FORMAT
        );
      }

      return {
        id,
        isRead: getTimeStamp(readDateTime) < Date.now(),
        title,
        content: body,
        expireDate,
        isCircular: thumbnailImage.isCircleImage, // 선물 이용권 or 뽑기권 중 확률이 가장 낮은 최대 리워드가 선물 이용권인 경우
        thumbnail: getImageUrl(thumbnailImage.url, {hasAlpha: true}),
        agoText: getTimeAgoText(
          {oldDate: receivedDateTime, now: Date.now()},
          t
        ),
        landingUrl: parseLandingUriFromServer(landingUrl),
      };
    }
  );
}

export function getWaitfreeList(
  t: TFunction,
  waitfreeList: I_Waitfree[] = []
): IWaitfreeContent[] {
  return waitfreeList.map(
    ({content, rechargesDateTime, interval, responseDateTime}) => {
      const {totalSeconds} = getDuration(interval);
      /*
    TODO : 7일 23시간과 같이 일/시간/분으로 나누어지지 않는 경우도 있다
    향후 7일 23시간마다 무료와 같이 변경될 수 있으나 현재는 앱과 맞추기 위해 버림으로 진행 (7일만 표시)
    */
      const intervalDay = Math.trunc(totalSeconds / SECONDS_OF_A_DAY);
      const intervalHour = Math.trunc(totalSeconds / SECONDS_OF_AN_HOUR);
      const intervalMinute = totalSeconds / 60;

      return {
        ...getContentProps(content, ['title', 'mainImg', 'id', 'sid', 'bgImg']),
        leftText: getRemainText(
          {
            endDate: getTimeStamp(rechargesDateTime),
            now: getNowFromServer(responseDateTime),
            isShortUnit: true,
            translationKey: 'contenthome_ticketbar_charging',
          },
          t
        ),
        interval:
          intervalDay >= 1
            ? t('contenthome_ticketbar_gidamu', {count: intervalDay})
            : intervalHour >= 1
              ? t('contenthome_ticketbar_gidamu_hour', {count: intervalHour})
              : t('contenthome_ticketbar_gidamu_minute', {
                  count: intervalMinute,
                }),
      };
    }
  );
}

export interface I_ThirdPartyTerm {
  termOfUseId: number;
  termVersion: string;
  title: string;
  messageKey: string;
  required: string;
  appOnly: string;
  accepted: string;
  acceptedDt: string;
}

interface I_MyNews {
  id: string;
  type: string;
  title: string;
  body: string;
  thumbnailImage: {
    isCircleImage: boolean;
    url: string;
  };
  landingUrl?: string;
  receivedDateTime: string;
  readDateTime: string;
  data?: {
    expireDateTime?: string;
    duration?: string;
  };
}

interface I_Waitfree {
  content: I_ContentMeta;
  interval: string;
  rechargesDateTime: string;
  responseDateTime?: string;
}

export function getAddedCashItems({
  reports = [],
}: {
  reports: I_AddedCashItem[];
}): IAddedCashItem[] {
  return reports.map(item => {
    const type = toCamelCase(item.chargeType) as IAddedCashType;
    return {
      creditId: item.creditId,
      cash: item.cashAmount,
      purchasedCash: item.price,
      date: formatFromServerDate(item.chargedAt, DEFAULT_SHORT_DATETIME_FORMAT),
      eventTitle: item.eventName,
      type,
      isReceipt: item.cashReceipt,
      expiredDate: formatFromServerDate(item.expiredDt, SHORT_DATE_FORMAT),
    };
  });
}

interface I_AddedCashItem {
  creditId: number;
  chargeType: string;
  eventName: string;
  price: number;
  cashAmount: number;
  cashReceipt: boolean;
  displayDetailButton: boolean;
  chargedAt: string;
  expiredDt: string;
}

export function getUsedCashItems(data: I_UsedCashItem[] = []): IUsedCashItem[] {
  return data.map(item => ({
    ...getContentProps(item.content, ['title']),
    cash: item.price,
    date: formatFromServerDate(
      item.issuedDateTime,
      DEFAULT_SHORT_DATETIME_FORMAT
    ),
    type: toCamelCase(item.ticketType) as IUsedCashType,
    ticketCount: item.ticketCount,
    isRefund: item.refunded,
    refundDate: formatFromServerDate(
      item.refundedDateTime,
      DEFAULT_SHORT_DATETIME_FORMAT
    ),
  }));
}

interface I_UsedCashItem {
  content: I_ContentMeta;
  price: number;
  issuedDateTime: string;
  ticketType: string;
  ticketCount: number;
  refunded: boolean;
  refundedDateTime: string;
}

export function getAddedDaumCashItems(
  data: I_AddedDaumCashItem[] = []
): IAddedDaumCashItem[] {
  return data.map(item => ({
    id: item.id,
    chargeAmount: item.chargeAmount,
    balance: item.balance,
    chargeDate: formatFromServerDate(item.chargeDate),
  }));
}

interface I_AddedDaumCashItem {
  balance: number;
  chargeAmount: number;
  chargeDate: string;
  id: number;
}

export function getUsedDaumCashItems(
  data: I_UsedDaumCashItem[] = []
): IUsedDaumCashItem[] {
  return data.map(item => ({
    id: item.id,
    orderDate: formatFromServerDate(item.orderDate, DEFAULT_DATE_FORMAT),
    productName: item.productName,
    orderEndDate: formatFromServerDate(item.orderEndDate, DEFAULT_DATE_FORMAT),
    productPrice: item.productPrice,
  }));
}

interface I_UsedDaumCashItem {
  id: number;
  orderDate: string;
  productName: string;
  orderEndDate: string;
  productPrice: number;
}

export function getUsedTicketHistory(
  items: I_UsedTicketItem[] = []
): IUsedTicketItem[] {
  return items.map(item => ({
    content: getContentProps(item.content, ['id', 'sid', 'title']),
    episode: {
      title: item.episode?.title || '',
    },
    type: toCamelCase(item.ticketType) as ITicketType,
    date: formatFromServerDate(
      item.usedDateTime,
      DEFAULT_SHORT_DATETIME_FORMAT
    ),
  }));
}

interface I_UsedTicketItem {
  ticketType: string;
  usedDateTime: string;
  licenseExpiresDateTime: string;
  content: I_ContentMeta;
  episode: I_EpisodeItem;
}

export function getAddedTicketHistory(
  data: I_AddedTicketItem[] = []
): IAddedTicketItem[] {
  return data.map(item => {
    const type = toCamelCase(item.ticketType) as ITicketType;
    return {
      cash: item.price,
      dateTime: formatFromServerDate(
        item.issuedDateTime,
        DEFAULT_SHORT_DATETIME_FORMAT
      ),
      type,
      ticketCount: item.ticketCount,
      isWithdraw: item.refunded,
      isExpired: item.expired,
      content: getContentProps(item.content, ['id', 'sid', 'title']),
      giftExpirationDate:
        type === 'gift'
          ? formatFromServerDate(item.expiresDateTime, SHORT_DATE_FORMAT)
          : undefined,
      refundedDateTime: item.refundedDateTime
        ? formatFromServerDate(
            item.refundedDateTime,
            DEFAULT_SHORT_DATETIME_FORMAT
          )
        : undefined,
    };
  });
}

interface I_AddedTicketItem {
  price: number;
  refunded: boolean;
  expired: boolean;
  ticketCount: number;
  ticketType: string;
  issuedDateTime: string;
  expiresDateTime: string;
  refundedDateTime?: string;
  content: I_ContentMeta;
}
